import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {CaretDown, CaretRight} from '../../icons';
import {Spinner} from '../../Spinner';
import {ClientEconomicsApi} from '../../../api/clientEconomicsApi';
import {
    PROFILE_FIELDS,
    SOURCE_LABEL_KEYS,
    formatEconomyDate,
    formatProfileValue,
} from '../panel_v11/economyFormatters';

const DIFF_FIELDS = new Set([...PROFILE_FIELDS, 'currency']);

/**
 * «Versiones» (plegado): cada guardado es una versión nueva con su vigencia, y el histórico se
 * calcula con la que regía ese mes. Se pide RECIÉN al desplegarla —es historia, no lo que se viene a
 * cargar— salvo que se llegue con `#versiones` (el link «Ver versiones anteriores» de la card).
 *
 * `refreshToken` cambia cuando la pantalla guarda una versión nueva: si la lista está abierta, se
 * vuelve a pedir; si está plegada, se pedirá al abrirla.
 */
export function EconomiaVersions({clientId, currency, initiallyOpen = false, refreshToken = 0, onLoaded}) {
    const {t, i18n} = useTranslation();
    const [open, setOpen] = useState(initiallyOpen);
    const [state, setState] = useState({loading: false, failed: false, versions: null});
    // En un ref y no en las dependencias del `load`: un callback inline del padre cambiaría en cada
    // render y el efecto de abajo volvería a pedir la lista en un bucle.
    const onLoadedRef = useRef(onLoaded);
    onLoadedRef.current = onLoaded;

    const load = useCallback(() => {
        setState({loading: true, failed: false, versions: null});
        ClientEconomicsApi.getVersions(clientId).then((response) => {
            const versions = response && response.status === 'OK' && Array.isArray(response.data)
                ? response.data : null;
            setState({loading: false, failed: versions === null, versions});
            if (versions && onLoadedRef.current) onLoadedRef.current(versions);
        }).catch(() => setState({loading: false, failed: true, versions: null}));
    }, [clientId]);

    useEffect(() => {
        if (open) load();
    }, [open, load, refreshToken]);

    const sourceLabel = (version) => {
        const key = SOURCE_LABEL_KEYS[version.source];
        return key ? t(key) : (version.source_label || version.source || '—');
    };

    const renderDiff = (version) => {
        const diff = version.diff_vs_previous || {};
        // La moneda de ESA versión (el backend la manda en `version.currency`): una versión vieja en
        // otra moneda no se formatea con la actual (R-ecofe-28).
        const money = version.currency || (version.values && version.values.currency) || currency;
        const fields = Object.keys(diff).filter((field) => DIFF_FIELDS.has(field));
        if (!fields.length) return null;
        return (
            <ul className="eco-version__diff">
                {fields.map((field) => {
                    const [before, after] = diff[field] || [];
                    const format = (value) => (field === 'currency' ? (value || '—')
                        : (formatProfileValue(field, value, money) || '—'));
                    return (
                        <li key={field}>
                            {t('economics.versions.diff', {
                                field: field === 'currency' ? t('economics.form.currency')
                                    : t(`client_panel.economy.input.${field}`),
                                before: format(before),
                                after: format(after),
                                interpolation: {escapeValue: false},
                            })}
                        </li>
                    );
                })}
            </ul>
        );
    };

    const renderBody = () => {
        if (state.loading) return <div className="box-loader"><Spinner color="positive small"/></div>;
        if (state.failed) {
            return (
                <p className="eco-fs__help">
                    {t('economics.page.load_error')}{' '}
                    <button type="button" className="eco-link" onClick={load}>{t('client_panel.error_retry')}</button>
                </p>
            );
        }
        if (!state.versions) return null;
        if (!state.versions.length) return <p className="eco-fs__help">{t('economics.versions.empty')}</p>;
        return (
            <ol className="eco-versions__list">
                {state.versions.map((version) => (
                    <li className="eco-version" key={version.id} data-testid={`eco-version-${version.id}`}>
                        <span className="eco-version__head">
                            {t('client_panel.economy.subtitle_source', {
                                source: sourceLabel(version),
                                version: version.version || '—',
                                date: formatEconomyDate(version.effective_from, i18n && i18n.language) || '—',
                                interpolation: {escapeValue: false},
                            })}
                            {version.created_by && <span className="eco-version__by">{` · ${version.created_by}`}</span>}
                        </span>
                        {renderDiff(version)}
                    </li>
                ))}
            </ol>
        );
    };

    return (
        <section className="eco-fs eco-versions" id="versiones" data-testid="eco-versions">
            <button type="button" className="eco-versions__toggle" aria-expanded={open}
                    onClick={() => setOpen((current) => !current)}>
                {open ? <CaretDown size={13} weight="bold"/> : <CaretRight size={13} weight="bold"/>}
                <span className="eco-fs__title">{t('economics.versions.title')}</span>
            </button>
            {open && <div className="eco-versions__body">{renderBody()}</div>}
        </section>
    );
}

export default EconomiaVersions;
